import type { FC, ReactNode } from 'react'
import type { GetStaticProps } from 'next'
import type { CourseOption } from '@/types'

import { CalendarCheck, GraduationCap, Send } from 'lucide-react'
import { getCourseOptions } from '@/lib/courses'
import { loadPageData, REVALIDATE_SECONDS, RETRY_REVALIDATE_SECONDS } from '@/lib/page-data'
import { Container } from '@/components/generals/container'
import { Layout } from '@/components/generals/layout'
import { SectionHeading } from '@/components/generals/section-heading'
import { Seo } from '@/components/generals/seo'
import { EnrollForm } from '@/components/enroll/enroll-form'

interface EnrollPageProps {
    courses: Array<CourseOption>
}

const STEPS = [
    {
        icon: Send,
        title: "Send your request",
        text: "Pick a course and a class time, or ask us to help you choose."
    },
    {
        icon: CalendarCheck,
        title: "Talk to an advisor",
        text: "Within one working day we confirm your level, your batch and the fee."
    },
    {
        icon: GraduationCap,
        title: "Start learning",
        text: "Join your first class with your own computer ready and waiting."
    }
]

const EnrollPage: FC<EnrollPageProps> = ({ courses }): ReactNode => {
    return (
        <Layout>
            <Seo
                title="Enroll"
                description="Request a seat in a ByteWise Academy computer course. Choose your course and preferred class time, and an advisor will contact you within one working day."
            />

            <section id='enroll' className='relative overflow-hidden'>
                <div
                    aria-hidden='true'
                    className='bg-dots pointer-events-none absolute inset-0 text-foreground/40 [mask-image:radial-gradient(ellipse_60%_70%_at_0%_0%,black,transparent)]'
                />
                <div aria-hidden='true' className='pointer-events-none absolute -right-40 -bottom-40 size-[32rem] rounded-full bg-primary/10 blur-3xl' />

                <Container className='relative grid gap-12 py-12 sm:py-16 lg:grid-cols-[minmax(0,0.85fr)_minmax(0,1.15fr)] lg:gap-16 lg:py-20'>
                    <div className='lg:pt-4'>
                        <SectionHeading
                            as='h1'
                            eyebrow="Enroll"
                            title="Save your seat in the next batch"
                            description="Fill in the form and an advisor will contact you within one working day to confirm your course, class time and fee. Sending it is free and does not commit you to anything."
                        />

                        <ol className='mt-10 space-y-5'>
                            {STEPS.map(({ icon: Icon, title, text }, index) => (
                                <li key={title} className='flex gap-4'>
                                    <span className='relative flex size-11 shrink-0 items-center justify-center rounded-xl bg-secondary text-primary'>
                                        <Icon className='size-5' aria-hidden='true' />
                                        <span className='absolute -top-1.5 -right-1.5 flex size-5 items-center justify-center rounded-full bg-accent font-mono text-[10px] font-semibold text-accent-foreground' aria-hidden='true'>
                                            {index + 1}
                                        </span>
                                    </span>

                                    <div>
                                        <h2 className='text-base font-semibold'>
                                            {title}
                                        </h2>

                                        <p className='mt-0.5 text-sm leading-relaxed text-muted-foreground'>
                                            {text}
                                        </p>
                                    </div>
                                </li>
                            ))}
                        </ol>
                    </div>

                    <div id='enroll-form' className='scroll-mt-24'>
                        <EnrollForm courses={courses} />
                    </div>
                </Container>
            </section>
        </Layout>
    )
}

export const getStaticProps: GetStaticProps<EnrollPageProps> = async () => {
    const courses = await loadPageData('enroll', getCourseOptions, [])

    return {
        props: {
            courses: courses.data
        },
        revalidate: courses.failed ? RETRY_REVALIDATE_SECONDS : REVALIDATE_SECONDS
    }
}

export default EnrollPage
